/**
 * PWAStatus - Zeigt PWA-Status und Offline-Funktionen an
 */

import { useState, useEffect } from 'react';
import { 
  Wifi, WifiOff, Download, RefreshCw, Cloud, CloudOff, 
  Smartphone, CheckCircle, Clock, Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import usePWA from '@/hooks/usePWA';
import useOfflineData from '@/hooks/useOfflineData';

export default function PWAStatus({ showDetails = false }) {
  const { 
    isOnline, 
    isInstallable, 
    isInstalled, 
    swUpdateAvailable,
    installApp, 
    updateServiceWorker 
  } = usePWA();

  const { 
    syncStatus, 
    isSyncing, 
    syncAll 
  } = useOfflineData();

  const [showPanel, setShowPanel] = useState(false);

  // Bei Statusänderung toasten
  useEffect(() => {
    if (!isOnline) {
      toast.warning('Sie sind offline. Gespeicherte Daten sind verfügbar.', {
        duration: 5000
      });
    }
  }, [isOnline]);

  // Update-Banner
  useEffect(() => {
    if (swUpdateAvailable) {
      toast.info('Ein Update ist verfügbar!', {
        action: {
          label: 'Aktualisieren',
          onClick: updateServiceWorker
        },
        duration: 10000
      });
    }
  }, [swUpdateAvailable, updateServiceWorker]);

  const handleInstall = async () => {
    const success = await installApp();
    if (success) {
      toast.success('Olympus wurde installiert!');
    }
  };

  const handleSync = async () => {
    toast.loading('Synchronisiere Daten...', { id: 'sync' });
    await syncAll();
    toast.success('Daten synchronisiert!', { id: 'sync' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nie';
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Minimale Statusanzeige in der Navbar
  if (!showDetails) {
    return (
      <div className="flex items-center gap-2">
        {/* Online/Offline Indikator */}
        <button
          onClick={() => setShowPanel(!showPanel)}
          className={`p-2 rounded-lg transition-colors ${
            isOnline 
              ? 'text-green-600 hover:bg-green-50' 
              : 'text-orange-600 bg-orange-50 hover:bg-orange-100'
          }`}
          title={isOnline ? 'Online' : 'Offline'}
        >
          {isOnline ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
        </button>

        {/* Install Button (nur wenn verfügbar) */}
        {isInstallable && !isInstalled && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleInstall}
            className="gap-1 text-purple-600 border-purple-200 hover:bg-purple-50"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Installieren</span>
          </Button>
        )}

        {/* Status Panel */}
        {showPanel && (
          <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border p-4 z-50">
            <div className="space-y-4">
              {/* Status Header */}
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">Offline-Status</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  isOnline 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-orange-100 text-orange-700'
                }`}>
                  {isOnline ? 'Online' : 'Offline'}
                </span>
              </div>

              {/* Sync Status */}
              {syncStatus && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Klienten</span>
                    <span className="text-slate-800 font-medium">
                      {syncStatus.klienten?.count || 0} gespeichert
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Aufgaben</span>
                    <span className="text-slate-800 font-medium">
                      {syncStatus.aufgaben?.count || 0} gespeichert
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Fristen</span>
                    <span className="text-slate-800 font-medium">
                      {syncStatus.fristen?.count || 0} gespeichert
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    Letzte Sync: {formatDate(syncStatus.klienten?.lastSync)}
                  </div>
                </div>
              )}

              {/* Aktionen */}
              <div className="flex gap-2 pt-2 border-t">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleSync}
                  disabled={isSyncing || !isOnline}
                  className="flex-1"
                >
                  {isSyncing ? (
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Cloud className="w-4 h-4 mr-1" />
                  )}
                  Synchronisieren
                </Button>
              </div>

              {/* Install Info */}
              {isInstalled && (
                <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded-lg">
                  <Smartphone className="w-4 h-4" />
                  App installiert
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Detaillierte Ansicht (für Einstellungen)
  return (
    <div className="space-y-6" data-testid="pwa-status-details">
      {/* Status Karten */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Online Status */}
        <div className={`p-4 rounded-xl border ${
          isOnline ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'
        }`}>
          <div className="flex items-center gap-3">
            {isOnline ? (
              <Wifi className="w-8 h-8 text-green-600" />
            ) : (
              <WifiOff className="w-8 h-8 text-orange-600" />
            )}
            <div>
              <p className="font-semibold text-slate-800">
                {isOnline ? 'Online' : 'Offline'}
              </p>
              <p className="text-sm text-slate-500">
                {isOnline ? 'Verbunden' : 'Keine Verbindung'}
              </p>
            </div>
          </div>
        </div>

        {/* Installation Status */}
        <div className={`p-4 rounded-xl border ${
          isInstalled ? 'bg-purple-50 border-purple-200' : 'bg-slate-50 border-slate-200'
        }`}>
          <div className="flex items-center gap-3">
            <Smartphone className={`w-8 h-8 ${isInstalled ? 'text-purple-600' : 'text-slate-400'}`} />
            <div>
              <p className="font-semibold text-slate-800">
                {isInstalled ? 'Installiert' : 'Web-Version'}
              </p>
              <p className="text-sm text-slate-500">
                {isInstalled ? 'Als App' : 'Im Browser'}
              </p>
            </div>
          </div>
        </div>

        {/* Sync Status */}
        <div className="p-4 rounded-xl border bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <Database className="w-8 h-8 text-blue-600" />
            <div>
              <p className="font-semibold text-slate-800">
                {syncStatus?.klienten?.count || 0} Klienten
              </p>
              <p className="text-sm text-slate-500">
                Offline verfügbar
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Detaillierte Sync-Info */}
      {syncStatus && (
        <div className="bg-white rounded-xl border p-4">
          <h4 className="font-semibold text-slate-800 mb-3">Offline-Datenspeicher</h4>
          <div className="space-y-3">
            {[
              { key: 'klienten', label: 'Klienten' },
              { key: 'aufgaben', label: 'Aufgaben' },
              { key: 'fristen', label: 'Fristen' }
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <p className="font-medium text-slate-700">{item.label}</p>
                  <p className="text-xs text-slate-400">
                    Letzte Aktualisierung: {formatDate(syncStatus[item.key]?.lastSync)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-slate-800">
                    {syncStatus[item.key]?.count || 0}
                  </span>
                  {syncStatus[item.key]?.lastSync && (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Aktionen */}
      <div className="flex flex-wrap gap-3">
        <Button 
          onClick={handleSync} 
          disabled={isSyncing || !isOnline}
          className="gap-2"
        >
          {isSyncing ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Cloud className="w-4 h-4" />
          )}
          Jetzt synchronisieren
        </Button>

        {isInstallable && !isInstalled && (
          <Button variant="outline" onClick={handleInstall} className="gap-2">
            <Download className="w-4 h-4" />
            App installieren
          </Button>
        )}

        {swUpdateAvailable && (
          <Button variant="secondary" onClick={updateServiceWorker} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Update installieren
          </Button>
        )}
      </div>

      {/* Info-Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h5 className="font-medium text-blue-800 mb-2">Offline-Funktionen</h5>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Klientenliste und Akten auch ohne Internet einsehbar</li>
          <li>• Aufgaben und Fristen werden lokal gespeichert</li>
          <li>• Automatische Synchronisation bei Verbindung</li>
          <li>• Installierbar als App auf iOS, Android und Desktop</li>
        </ul>
      </div>
    </div>
  );
}
